import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Save, Loader2, Check } from "lucide-react";

/**
 * Cloud runtime-tunables panel
 * ----------------------------
 * Surfaces the per-cloud knobs the worker re-applies to NC config.php
 * on every config_version bump:
 *
 *   - maintenance_window_start  (0-23 UTC hour)
 *   - default_phone_region      (ISO-3166-1 alpha-2, e.g. "DE", "AT")
 *
 * Both are nullable on the cloud row; null means "follow the global
 * cm_settings default". Saving sends a PATCH /api/admin/clouds/{id}
 * which both persists the value AND bumps cloud_configs.version → the
 * next worker heartbeat re-applies the OCC commands AND re-pushes
 * config.php → web VMs auto-re-pull on their next 30s poll.
 */

const COMMON_REGIONS = [
    { code: "DE", label: "Germany" },
    { code: "AT", label: "Austria" },
    { code: "CH", label: "Switzerland" },
    { code: "FR", label: "France" },
    { code: "NL", label: "Netherlands" },
    { code: "GB", label: "United Kingdom" },
    { code: "US", label: "United States" },
    { code: "IT", label: "Italy" },
    { code: "ES", label: "Spain" },
    { code: "PL", label: "Poland" },
];

export default function CloudTunablesPanel({ cloudId, cloud, onSaved }) {
    const [mw, setMw] = useState(
        cloud.maintenance_window_start ?? "",
    );
    const [region, setRegion] = useState(
        cloud.default_phone_region || "",
    );
    const [saving, setSaving] = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);
    const [err, setErr] = useState(null);

    // Track which incoming values we've reflected so SWR refreshes don't
    // clobber operator edits. Compare on the prop directly instead of
    // re-syncing through a state-in-effect.
    const propMw = cloud.maintenance_window_start ?? "";
    const propRegion = cloud.default_phone_region || "";

    const dirty =
        String(mw) !== String(propMw) ||
        (region || "") !== propRegion;

    const save = async () => {
        setSaving(true);
        setErr(null);
        try {
            const payload = {};
            if (String(mw) !== String(propMw)) {
                payload.maintenance_window_start = mw === "" ? null : parseInt(mw, 10);
            }
            if ((region || "") !== propRegion) {
                payload.default_phone_region = region === "" ? null : region.toUpperCase();
            }
            await api.patch(`/api/admin/clouds/${cloudId}`, payload);
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 1800);
            onSaved?.();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="cm-card p-5" data-testid="cloud-tunables-panel">
            <div className="cm-h3 mb-3">Cloud tunables</div>
            <div className="text-[11px] text-zinc-500 mb-4 font-mono">
                Bumps cloud_configs.version → worker re-applies OCC on the next heartbeat → webs follow.
            </div>

            <label className="block mb-3">
                <div className="cm-label mb-1.5">Maintenance window (UTC hour)</div>
                <select
                    data-testid="tunable-mw-input"
                    value={mw}
                    onChange={(e) => setMw(e.target.value)}
                    className="cm-input text-xs w-full"
                    disabled={saving}
                >
                    <option value="">— global default —</option>
                    {Array.from({ length: 24 }).map((_, i) => (
                        <option key={i} value={i}>
                            {String(i).padStart(2, "0")}:00 – {String(i).padStart(2, "0")}:59 UTC
                        </option>
                    ))}
                </select>
                <div className="text-[11px] text-zinc-600 mt-1 font-mono">
                    Cron for DB optimize / preview pregen runs only in this hour.
                </div>
            </label>

            <label className="block mb-3">
                <div className="cm-label mb-1.5">Default phone region (ISO 3166-1)</div>
                <select
                    data-testid="tunable-region-input"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    className="cm-input text-xs w-full"
                    disabled={saving}
                >
                    <option value="">— global default (DE) —</option>
                    {COMMON_REGIONS.map((r) => (
                        <option key={r.code} value={r.code}>
                            {r.code} — {r.label}
                        </option>
                    ))}
                </select>
                <div className="text-[11px] text-zinc-600 mt-1 font-mono">
                    Phone numbers without a country code are validated against this region.
                </div>
            </label>

            <button
                type="button"
                data-testid="tunable-save-button"
                onClick={save}
                disabled={!dirty || saving}
                className="cm-btn-primary w-full text-xs flex items-center justify-center gap-1.5 disabled:opacity-30"
            >
                {saving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : savedFlash ? (
                    <Check className="w-3.5 h-3.5" />
                ) : (
                    <Save className="w-3.5 h-3.5" />
                )}
                <span>
                    {saving
                        ? "Saving…"
                        : savedFlash
                            ? "Saved — workers syncing…"
                            : dirty
                                ? "Save + resync"
                                : "No changes"}
                </span>
            </button>

            {err && (
                <div className="mt-2 text-xs text-red-400 font-mono break-words">
                    {err}
                </div>
            )}
        </div>
    );
}
